import React, { useState } from 'react'
import { Box, Button, Divider, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material'
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded'
import { ComponentTicket } from '../api/useComponentTickets'
import { formatDayTime } from '../format'
import { advancedColors } from '../theme'

type Props = {
  label: string
  tickets: ComponentTicket[]
  onSubmit: (text: string) => void
}

const ComponentNoteButton = ({ label, tickets, onSubmit }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [draft, setDraft] = useState('')

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft('')
    setAnchor(event.currentTarget)
  }

  const save = () => {
    onSubmit(draft.trim())
    setDraft('')
  }

  return (
    <>
      <Tooltip title={tickets.length > 0 ? 'View or leave feedback' : 'Leave feedback on this component'}>
        <IconButton
          size="small"
          aria-label={`Leave feedback on ${label}`}
          onClick={open}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
            color: tickets.length > 0 ? advancedColors.amber : advancedColors.inkMuted,
            backgroundColor: advancedColors.surface,
            '&:hover': { backgroundColor: advancedColors.surface },
          }}
        >
          <RateReviewRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            autoFocus
            multiline
            minRows={3}
            label={`Feedback — ${label}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button
            variant="contained"
            size="small"
            onClick={save}
            disabled={!draft.trim()}
            sx={{ alignSelf: 'flex-end' }}
          >
            Save
          </Button>

          {tickets.length > 0 && (
            <>
              <Divider />
              <Typography variant="overline" sx={{ color: advancedColors.inkMuted }}>
                Your tickets on this component
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxHeight: 240, overflowY: 'auto' }}>
                {[...tickets].reverse().map((ticket) => (
                  <Box key={ticket.id} sx={{ borderLeft: `2px solid ${advancedColors.line}`, pl: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {ticket.text}
                    </Typography>
                    <Typography variant="caption" sx={{ color: advancedColors.inkFaint }}>
                      {formatDayTime(ticket.submittedAt)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default ComponentNoteButton
